import type { Product, ProductDetailsPage } from "@decocms/apps-commerce/types";
import { mapProductToAnalyticsItem } from "@decocms/apps-commerce/utils/productToAnalyticsItem";
import { SendEventOnLoad } from "~/components/Analytics";
import ImageGallerySlider from "~/components/product/Gallery";
import Breadcrumb from "~/components/ui/Breadcrumb";
import Button from "~/components/ui/Button";

import AddToCartButtonVTEX from "~/components/product/AddToCartButton/vtex";

import OutOfStock from "~/components/product/OutOfStock";
import CashBackFlag from "~/components/product/CashBackFlag";
import FloatBuyButton from "~/components/product/FloatBuyButton";
import ShippingSimulation from "~/components/ui/ShippingSimulation";
import { clx } from "@decocms/blocks/sdk/clx";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { useEffect, useId, useRef, useState } from "react";
import { sendEvent } from "~/sdk/analytics";
import { useOffer } from "~/sdk/useOffer";

import ProductSelector from "./ProductVariantSelector";

export interface Props {
  /** @title Integration */
  page: ProductDetailsPage | null;

  layout?: {
    CashBackInfos?: {
      showCashBackFlag?: boolean;
      /**
       * @title Additional Simulation Text
       * @description Text to appear when the customer simulates the shipping
       */
      cashBackFlagPercentage?: number;
      /**
       * @title Cash Back Flag Percentage
       */
      cashBackCluster?: string;
      /**
       * @title Cash back collection
       */
      /**
       * @title Validate Cluster
       * @description If false, cashback will be shown for all products. If true, cashback will only be shown for products in the specified collection.
       * @default true
       */
      validateCluster?: boolean;
    };
    /**
     * @title Product Name
     * @description How product title will be displayed. Concat to concatenate product and sku names.
     * @default productGroup
     */
    name?: "concat" | "productGroup" | "product";
    /**
     * @title Show Slider Arrows
     * @description Show image slider arrows
     * @default false
     */
    showImageSliderArrows?: boolean;
    /**
     * @title Show Taxes Text
     * @description Show "com juros" or "sem juros" at installments
     * @default false
     */
    showTaxesText?: boolean;
    /**
     * @title Additional Simulation Text
     * @description Text to appear when the customer simulates the shipping
     */
    additionalSimulationText?: string;
    /**
     * @title Free Shipping Card Info
     */
    freeShippingCardInfo?: {
      /**
       * @title Title
       */
      freeShippingCardTitle: string;
      /**
       * @title Location
       */
      freeShippingCardLocation: string;
      /**
       * @title Condition
       */
      freeShippingCardCondition: string;
    };
  };
}

/**
 * Rendered when a not found is returned by any of the loaders run on this page
 */
function NotFound() {
  return (
    <div className="w-full flex justify-center items-center py-28">
      <div className="flex flex-col items-center justify-center gap-6">
        <span className="font-medium text-2xl">Página não encontrada</span>
        <a href="/">
          <Button aria-label="Voltar à página inicial">
            Voltar à página inicial
          </Button>
        </a>
      </div>
    </div>
  );
}

function ProductInfo(
  { page, layout, onSkuChange }:
    & { page: ProductDetailsPage; onSkuChange: (skuId: string) => void }
    & Props,
) {
const { breadcrumbList, product } = page;
  const {
    productID,
    offers,
    name = "",
    isVariantOf,
    additionalProperty = [],
  } = product;
  const {
    price = 0,
    listPrice,
    seller = "1",
    installments,
    availability,
  } = useOffer(offers, layout?.showTaxesText);
  const discount = price && listPrice ? listPrice - price : 0;
  const freeShippingCardInfo = layout?.freeShippingCardInfo;
  const additionalSimulationText = layout?.additionalSimulationText;
  const CashBackFlagPercentage = layout?.CashBackInfos?.cashBackFlagPercentage;
  const cashBackCluster = layout?.CashBackInfos?.cashBackCluster;
  const validateCluster = layout?.CashBackInfos?.validateCluster;

  const isAvailable = availability === "https://schema.org/InStock";

  let referenceId;

  for (const property of additionalProperty) {
    if (property.name === "RefId") {
      referenceId = property.value;
    }
  }

  const productName = layout?.name === "concat"
    ? `${isVariantOf?.name} ${name}`
    : layout?.name === "productGroup"
    ? isVariantOf?.name
    : name;

  // Primeiro `view_item` (SKU do carregamento inicial) já é coberto por
  // <SendEventOnLoad>, que depende do listener de `load` do navegador e só
  // dispara uma vez por carregamento real de página. Trocas de SKU
  // subsequentes (via replaceState, sem reload) não passam por `load`
  // de novo — este efeito cobre exatamente esse caso, pulando o primeiro
  // render pra não duplicar o evento do load inicial.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    sendEvent({
      name: "view_item",
      params: {
        items: [
          mapProductToAnalyticsItem({
            product,
            breadcrumbList,
            price,
            listPrice,
          }),
        ],
      },
    });
  }, [product.productID]);

  return (
    <>
      {/* Code, name and wishlist */}
      <div className="flex flex-col items-center w-full lg:items-start">
        <div className="flex justify-between lg:w-full">
          <h1 className="text-center m-0 lg:text-start">
            <span className="font-['Poppins','Times_New_Roman',Times,serif] not-italic font-normal text-[26px] leading-[31px] pr-40px mt-0 text-[#333333]">
              {productName}
            </span>
          </h1>
          {
            /* <WishlistButton
            variant="icon"
            productID={productID}
            productGroupID={productGroupID}
          /> */
          }
        </div>
        <div>
          {referenceId && (
            <span className="flex text-sm text-[#434343] uppercase lg:text-[11px] py-0">
              Cod: {referenceId?.slice(0, 5)}
            </span>
          )}
        </div>
      </div>
      {/* Prices */}
      {isAvailable && (
        <div className="w-full flex flex-col items-start mt-4">
          <div className="flex flex-col gap-2 mb-[5px] items-start lg:mb-0">
            {(listPrice ?? 0) > price && (
              <span className="line-through font-['Poppins','Times_New_Roman',Times,serif] font-normal text-[11px] leading-[27px] text-[#434343] lg:leading-none">
                {formatPrice(listPrice, offers?.priceCurrency)}
              </span>
            )}
            <span className="font-['Poppins','Times_New_Roman',Times,serif] font-medium text-[#333333] text-lg leading-[18px] lg:text-lg lg:leading-none">
              {formatPrice(price, offers?.priceCurrency)}
            </span>
          </div>
          <span className="font-['Poppins','Times_New_Roman',Times,serif] font-normal text-xs leading-[17px] text-[#434343]">
            {installments}
          </span>
          {layout?.CashBackInfos?.showCashBackFlag && (
            <span className="block min-h-6">
              <CashBackFlag
                cashBackFlagPercentage={CashBackFlagPercentage}
                product={product}
                cashBackCluster={cashBackCluster}
                validateCluster={validateCluster}
              />
            </span>
          )}
        </div>
      )}
      {/* Sku Selector */}
      <div className="w-full flex items-start pt-[15px]">
        <ProductSelector product={product} onSkuChange={onSkuChange} />
      </div>
      {/* Tabela de Medidas: link manual removido (2026-08-26) — o Sizebay
         (agora com o anchor `div#productSizeSelector` corrigido, ver
         ProductVariantSelector.tsx) injeta seu próprio botão "TABELA DE
         MEDIDAS" logo abaixo do seletor de tamanho; manter os dois ficaria
         redundante. Página `/tabela-de-medidas` continua existindo no CMS,
         só não tem mais esse link direto na PDP. */}
      {/* Modelo Veste */}
      {isVariantOf?.additionalProperty?.find((prop) =>
        prop.name === "Modelo veste"
      ) && (
        <div className="w-full flex items-start pt-2 lg:p-0">
          <span className="text-[13px] text-[#333333]">
            Modelo veste: {""}
            {isVariantOf.additionalProperty.find((prop) =>
              prop.name === "Modelo veste"
            )?.value}
          </span>
        </div>
      )}
      {/* Add to Cart button */}
      <div id="buyButton" className="mt-4 sm:mt-8 flex flex-col w-full gap-2">
        {isAvailable
          ? (
            <>
              <AddToCartButtonVTEX
                  product={product}
                  listPrice={listPrice!}
                  breadcrumbList={breadcrumbList}
                  price={price}
                  discount={discount}
                  seller={seller}
                  id="fixedBuyButton"
                  className="uppercase bg-[#1A1A1A] text-white w-full h-14 py-5 px-[35px] leading-[18px] text-[15.14px] font-['Poppins',Helvetica,Arial,sans-serif] font-semibold order-1 transition-[background-color] duration-[0.2s] ease-linear hover:bg-[#C18A58] hover:text-[#1A1A1A] lg:w-[341px]"
                />
                                                                    </>
          )
          : <OutOfStock productID={productID} />}
      </div>
      {/* Shipping Simulation */}
      <div className="mt-8 mb-4 w-full">
        <ShippingSimulation
            items={[
              {
                id: Number(product.sku),
                quantity: 1,
                seller: seller,
              },
            ]}
            additionalSimulationText={additionalSimulationText}
          />
      </div>
      {/* Free Shipping Card */}
      {freeShippingCardInfo && (
        <div className="text-[#828282] text-left flex flex-col items-center mt-0 mb-0 lg:items-start">
          <p className="flex flex-row tracking-[0.14rem] leading-[12.19px] gap-[5px] text-[11px] font-['Poppins',Helvetica,Arial,sans-serif]">
            <span className="text-[#333333] font-bold">
              {freeShippingCardInfo.freeShippingCardTitle}
            </span>
            <strong className="text-[#855E3B]">
              {freeShippingCardInfo.freeShippingCardLocation}
            </strong>
          </p>
          <p className="block tracking-[0.14rem] leading-[12.19px] text-[11px] font-['Poppins',Helvetica,Arial,sans-serif] text-[#333333]">
            {freeShippingCardInfo.freeShippingCardCondition}
          </p>
        </div>
      )}
      {/* Analytics Event */}
      <SendEventOnLoad
        event={{
          name: "view_item",
          params: {
            items: [
              mapProductToAnalyticsItem({
                product,
                breadcrumbList,
                price,
                listPrice,
              }),
            ],
          },
        }}
      />
    </>
  );
}

function Details(props: { page: ProductDetailsPage } & Props) {
  const id = useId();
  const {
    page,
    layout,
  } = props;

  const {
    product,
    product: { isVariantOf },
  } = page;

  // Seleção de SKU feita em ProductVariantSelector (replaceState, sem
  // navegação). undefined = confia no `product` vindo do servidor (primeiro
  // load, ou nenhum clique ainda). Reseta ao navegar de verdade pra outro
  // produto.
  const hasVariant = isVariantOf?.hasVariant ?? [];
  const [selectedSkuId, setSelectedSkuId] = useState<string | undefined>(
    undefined,
  );
  useEffect(() => {
    setSelectedSkuId(undefined);
  }, [product.productID]);

  const selectedVariant = selectedSkuId
    ? hasVariant.find((variant) => variant.productID === selectedSkuId)
    : undefined;
  // ProductLeaf (tipo de cada item de hasVariant) é Omit<Product,
  // "isVariantOf"> só a nível de tipo — em runtime, o objeto real de cada
  // variante carrega a própria chave `isVariantOf: undefined` (não ausente),
  // e o spread copia isso por cima do isVariantOf de `product`, zerando
  // `hasVariant` na árvore inteira. Por isso `isVariantOf` é reatribuído
  // explicitamente depois do spread, sempre a partir do `product` original
  // (dado por-grupo: descrição, modelagem — não varia por SKU).
  const effectiveProduct: Product = selectedVariant
    ? { ...product, ...selectedVariant, isVariantOf: product.isVariantOf }
    : product;
  const effectivePage: ProductDetailsPage = {
    ...page,
    product: effectiveProduct,
  };
  const images = effectiveProduct.image ?? [];

  const { price, listPrice, availability } = useOffer(
    effectiveProduct.offers,
    layout?.showTaxesText,
  );

  const isAvailable = availability === "https://schema.org/InStock";

  const productName = layout?.name === "concat"
    ? `${isVariantOf?.name} ${effectiveProduct.name}`
    : layout?.name === "productGroup"
    ? isVariantOf?.name
    : effectiveProduct.name;

  const _discountPercentage = listPrice && price && listPrice !== price
    ? 100 - Math.floor((price / listPrice) * 100)
    : 0;

  // Variables needed for accordions
  const description = product.description || isVariantOf?.description;
  const parsedDescription = description
    ?.replaceAll("\r\n", "<br />")
    // Some VTEX product descriptions carry a leftover "..." placeholder
    // (never filled in) plus a trail of empty line breaks — strip both
    // so the rendered description doesn't show a stray ellipsis or a
    // large empty gap before the next accordion section.
    .replace(/(?:<br\s*\/?>\s*)*\.{3,}\s*(?:<br\s*\/?>\s*)*/g, "<br />")
    .replace(/(?:<br\s*\/?>\s*){3,}/g, "<br /><br />")
    .replace(/^(?:<br\s*\/?>\s*)+|(?:<br\s*\/?>\s*)+$/g, "");

  const compositionProperty = isVariantOf?.additionalProperty?.find(
    (property) => property.name === "Composição do Tecido",
  );

  // Genius-SEO Description
  const geniusSeoDescription = (() => {
    try {
      const productAdditionalProperties = page.product.isVariantOf
        ?.additionalProperty;
      if (!productAdditionalProperties) return null;

      const geniusSeoProperty = productAdditionalProperties.find(
        (property) => property.propertyID === "Genius-SEO",
      );

      return geniusSeoProperty?.value || null;
    } catch (error) {
      console.error("Erro ao buscar descrição Genius SEO:", error);
      return null;
    }
  })();

  const isUniqueImage = (images?.length ?? 0) < 2;

  return (
    <>
      <div
        id={id}
        className="flex flex-col justify-center items-center mt-0 lg:flex-row lg:items-start max-w-[1350px] m-auto"
      >
        <div
          className={clx(
            "grid",
            "grid-cols-1 gap-3 py-0",
            `lg:grid-cols-[auto_auto] lg:gap-6 ${
              isUniqueImage ? "lg:justify-center" : "lg:justify-start"
            }`,
          )}
        >
          <div className="lg:col-start-1 lg:col-end-2">
            <ImageGallerySlider page={effectivePage} />
          </div>
        </div>
        {/* Product Info */}
        <div className="px-2.5 w-full flex flex-col items-center lg:items-start lg:max-w-[490px] sticky top-25">
          <ProductInfo
            page={effectivePage}
            layout={layout}
            onSkuChange={setSelectedSkuId}
          />
        </div>
      </div>
      <div className="w-full px-2.5 lg:px-0 flex flex-col items-center lg:items-start max-w-[1200px] m-auto">
        {/* Description */}
        <div className="mt-4 lg:mt-[54px] w-[95%] md:w-full">
          {parsedDescription && (
            <span className="text-sm">
              {description && (
                <details
                  className="collapse collapse-arrow font-['Poppins'] rounded-none"
                  open
                >
                  <summary className="collapse-title min-h-[41px] uppercase text-xs font-semibold leading-4 tracking-[0.1rem] py-2.5 px-0 border-b-[#F0F0F0] border-b-[1px] text-[#333333] font-['Poppins',sans-serif]">
                    Descrição
                  </summary>
                  <div className="my-5 mx-0 font-['Poppins',sans-serif]">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: geniusSeoDescription || parsedDescription,
                      }}
                    />
                  </div>
                </details>
              )}
            </span>
          )}
        </div>
        {/* Modelagem */}
        {isVariantOf?.additionalProperty?.find((prop) =>
          prop.name === "Modelagem"
        ) && (
          <div className="w-[95%] md:w-full">
            {product.alternateName && (
              <span className="text-sm">
                {product.alternateName && (
                  <details className="collapse collapse-arrow font-['Poppins'] rounded-none">
                    <summary className="collapse-title min-h-[41px] uppercase text-xs font-semibold leading-4 tracking-[0.1rem] py-2.5 px-0 border-b-[#F0F0F0] border-b-[1px] text-[#333333] font-['Poppins',sans-serif]">
                      Modelagem
                    </summary>
                    <p className="my-5 mx-0 text-sm text-[#333333] font-['Poppins',sans-serif]">
                      {isVariantOf.additionalProperty.find((prop) =>
                        prop.name === "Modelagem"
                      )?.value}
                    </p>
                  </details>
                )}
              </span>
            )}
          </div>
        )}
        {/* Composition */}
        <div className="w-[95%] md:w-full">
          {compositionProperty && (
            <span className="text-sm">
              {description && (
                <details className="collapse collapse-arrow font-['Poppins'] rounded-none">
                  <summary className="collapse-title min-h-[41px] uppercase text-xs font-semibold leading-4 tracking-[0.1rem] py-2.5 px-0 border-b-[#F0F0F0] border-b-[1px] text-[#333333] font-['Poppins',sans-serif]">
                    Composição
                  </summary>
                  <p className="my-5 mx-0 font-['Poppins',sans-serif]">
                    {compositionProperty.value}
                  </p>
                </details>
              )}
            </span>
          )}
        </div>

        {/* Trocas e Devoluções */}
        <div className="w-[95%] md:w-full">
          <span className="text-sm">
            <details className="collapse collapse-arrow font-['Poppins'] rounded-none">
              <summary className="collapse-title min-h-[41px] uppercase text-xs font-semibold leading-4 tracking-[0.1rem] py-2.5 px-0 border-b-[#F0F0F0] border-b-[1px] text-[#333333] font-['Poppins',sans-serif]">
                troca e devolução
              </summary>
              <div className="my-5 mx-0 font-['Poppins',sans-serif]">
                <p className="my-5 mx-0">
                  Compra garantida, receba a peça que você espera, ou solicite
                  seu dinheiro de volta.
                </p>
                <a
                  className="text-black underline"
                  href="/institucional/trocas-e-devolucoes"
                  target="_blank"
                >
                  Saiba mais
                </a>
              </div>
            </details>
          </span>
        </div>
      </div>
      <FloatBuyButton
        {...{ isAvailable }}
        platform="vtex"
        listPrice={listPrice!}
        price={price}
        images={images}
        productName={productName}
        offers={effectiveProduct.offers}
      />
    </>
  );
}

function ProductDetails({ page, layout }: Props) {
  return (
    <div className="w-full">
      {page
        ? (
          <>
            <div className="hidden lg:flex max-w-[1350px] m-auto pb-4">
              <Breadcrumb
                itemListElement={page?.breadcrumbList?.itemListElement.slice(
                  0,
                  -1,
                )}
                page="product"
              />
            </div>
            <Details page={page} layout={layout} />
            {" "}
          </>
        )
        : <NotFound />}
    </div>
  );
}

export default ProductDetails;
